import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { CaptainLogo } from "./CaptainLogo";
import { useIsMobile } from "@/hooks/use-mobile";

interface NavbarProps {
  lang: string;
  setLang: (lang: string) => void;
  onOpenChat?: () => void;
  t: (en: string, fr: string, es?: string) => string;
}

const navLinks = [
  { href: "#hero", labelEn: "Home", labelFr: "Accueil", labelEs: "Inicio" },
  { href: "#services", labelEn: "Services", labelFr: "Services", labelEs: "Servicios" },
  { href: "/crr-zone1", labelEn: "CRR Training", labelFr: "Formation CRR", labelEs: "Formación CRR" },
  { href: "/maritime-careers", labelEn: "Careers", labelFr: "Carrières", labelEs: "Carreras" },
  { href: "/scuba-diving", labelEn: "Scuba", labelFr: "Plongée", labelEs: "Buceo" },
  { href: "/dt-profiles", labelEn: "DT Profiles", labelFr: "Profils DT", labelEs: "Perfiles DT" },
  { href: "/guadeloupe", labelEn: "Guadeloupe", labelFr: "Guadeloupe", labelEs: "Guadalupe" },
  { href: "/contact", labelEn: "Contact", labelFr: "Contact", labelEs: "Contacto" },
];

export const Navbar = ({ lang, setLang, onOpenChat, t }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("#")) {
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border/40 shadow-lg"
          : "bg-transparent"
      }`}
    >
      {/* Top utility bar — desktop only */}
      {!isMobile && (
        <div className="border-b border-border/20">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-8">
            <span className="text-[10px] text-muted-foreground tracking-wide">
              {t("AI Maritime Companion", "Compagnon Maritime IA", "Compañero Marítimo IA")}
            </span>
            <div className="flex items-center gap-1">
              <Globe className="w-3 h-3 text-muted-foreground mr-1" />
              {(["es", "fr", "en"] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    lang === l
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main navbar */}
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-14">
        {/* Logo */}
        <a
          href="/"
          className="flex items-center gap-2.5 no-underline"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <CaptainLogo size={30} />
          <div className="leading-tight">
            <span className={`font-bold text-sm tracking-wide ${scrolled ? "text-foreground" : "text-white"}`}>
              Captain ROCA
            </span>
            {!isMobile && (
              <span className={`block text-[10px] ${scrolled ? "text-muted-foreground" : "text-white/60"}`}>
                #DyslexSEA
              </span>
            )}
          </div>
        </a>

        {/* Desktop nav links */}
        {!isMobile && (
          <div className="flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  if (link.href.startsWith("#")) {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors no-underline ${
                  scrolled
                    ? "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {t(link.labelEn, link.labelFr, link.labelEs)}
              </a>
            ))}
          </div>
        )}

        {/* Right side: Chat CTA + mobile hamburger */}
        <div className="flex items-center gap-2">
          {/* Language switcher on mobile (moved from utility bar) */}
          {isMobile && (
            <div className="flex items-center gap-0.5 mr-1">
              {(["es", "fr", "en"] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-1.5 py-1 rounded text-[10px] font-medium transition-colors ${
                    lang === l
                      ? "bg-primary text-primary-foreground"
                      : scrolled ? "text-muted-foreground" : "text-white/60"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {isMobile && (
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition-colors ${
                scrolled ? "text-foreground hover:bg-secondary" : "text-white hover:bg-white/10"
              }`}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {isMobile && mobileOpen && (
        <div className="bg-background/98 backdrop-blur-lg border-t border-border/30 animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  if (link.href.startsWith("#")) {
                    e.preventDefault();
                  }
                  handleNavClick(link.href);
                }}
                className="block px-4 py-3 rounded-lg text-sm font-medium text-foreground hover:bg-secondary/50 transition-colors no-underline"
              >
                {t(link.labelEn, link.labelFr, link.labelEs)}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};
